import { type ZodIssue } from 'zod'
import { type OnboardingValidationFailureReason } from '#app/utils/pz/analytics.catalog'
import {
	EmailSchema,
	NameSchema,
	PASSWORD_MAX_BYTES,
	PASSWORD_MIN_LENGTH,
	UsernameSchema,
} from '#app/utils/user-validation'

/**
 * Pure classifiers for the PRD 038 friction analytics events.
 *
 * They derive a stable reason code from the raw entered value and the
 * untranslated validation schemas (zod issue codes), never from the rendered
 * error message text, so locale changes cannot break the analytics.
 */

function isBlank(value: unknown): boolean {
	return typeof value !== 'string' || value.trim().length === 0
}

function zodIssueToReason(issue: ZodIssue): OnboardingValidationFailureReason {
	switch (issue.code) {
		case 'too_small':
			return 'too_short'
		case 'too_big':
			return 'too_long'
		case 'invalid_string':
			return 'invalid_format'
		case 'invalid_type':
			return 'required'
		default:
			return 'invalid'
	}
}

export function classifySignupEmailFriction(
	rawEmail: unknown,
): 'required' | 'invalid_format' | 'email_exists' {
	if (isBlank(rawEmail)) return 'required'
	const parsed = EmailSchema.safeParse(rawEmail)
	// A schema-valid email can only fail server-side because the account
	// already exists (the async superRefine in the signup action).
	return parsed.success ? 'email_exists' : 'invalid_format'
}

export type OnboardingFrictionField =
	| 'username'
	| 'name'
	| 'password'
	| 'confirm_password'
	| 'terms'

export function classifyOnboardingFieldFriction(
	field: OnboardingFrictionField,
	values: {
		username?: unknown
		name?: unknown
		password?: unknown
		confirmPassword?: unknown
	},
): OnboardingValidationFailureReason {
	switch (field) {
		case 'username': {
			if (isBlank(values.username)) return 'required'
			const parsed = UsernameSchema.safeParse(values.username)
			if (parsed.success) return 'username_exists'
			const issue = parsed.error.issues[0]
			return issue ? zodIssueToReason(issue) : 'invalid'
		}
		case 'name': {
			if (isBlank(values.name)) return 'required'
			const parsed = NameSchema.safeParse(values.name)
			if (parsed.success) return 'invalid'
			const issue = parsed.error.issues[0]
			return issue ? zodIssueToReason(issue) : 'invalid'
		}
		case 'password': {
			// The max-length rule is a zod refine (code 'custom'), so the byte
			// check is applied directly instead of through the issue code.
			const password =
				typeof values.password === 'string' ? values.password : ''
			if (password.length === 0) return 'required'
			if (password.length < PASSWORD_MIN_LENGTH) return 'too_short'
			if (new TextEncoder().encode(password).length > PASSWORD_MAX_BYTES) {
				return 'too_long'
			}
			// A schema-valid password can only fail server-side via the common
			// password check.
			return 'password_too_common'
		}
		case 'confirm_password': {
			if (isBlank(values.confirmPassword)) return 'required'
			return values.confirmPassword === values.password ? 'invalid' : 'mismatch'
		}
		case 'terms':
			return 'not_accepted'
	}
}
